/** Tipps, Checkliste „Erste Schritte“ und die Hilfe hinter dem „?“. Reine Oberfläche, keine Spielzüge. */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useGame, useS, getLocalRole } from '../../store/game';
import { chancellorScreens } from '../multiplayer/Versus';
import { Panel } from '../../components/ui';
import { track } from '../../lib/telemetry';
import { useTutorial } from './store';
import { nextTip, screenOf, textOf, tipsFor, TIPS, type Ctx, type Role } from './tips';

/** Aktueller Bildschirm und Rolle des Spielers an diesem Gerät. */
export function useTipContext(): { c: Ctx; screen: string } {
  const S: any = useS();
  const versus = !!S.mp && S.mp.mode === 'versus';
  const guest = versus && !chancellorScreens(S);
  const local = getLocalRole();
  const r2 = S.mp?.pl?.[local]?.role2;
  const role: Role = !guest ? 'chan' : r2 === 'partner' ? 'partner' : r2 === 'opposition' && S.phase === 'govern' ? 'opp' : 'player';
  return { c: { S, role, versus }, screen: screenOf(S, role, guest, local) };
}

type Pos = { top: number; left: number; arrow: number; side: 'below' | 'above' | 'inside' };
const GAP = 12, MARGIN = 12;

/** Popup neben dem Ziel platzieren: bevorzugt darunter, sonst darüber, bei sehr großen Zielen oben im Ziel. */
export function place(r: { top: number; bottom: number; left: number; width: number }, w: number, h: number, vw: number, vh: number): Pos {
  const left = Math.min(Math.max(MARGIN, r.left), Math.max(MARGIN, vw - w - MARGIN));
  const arrow = Math.min(Math.max(18, r.left + Math.min(r.width, 240) / 2 - left), w - 18);
  if (r.bottom + GAP + h <= vh - MARGIN) return { top: r.bottom + GAP, left, arrow, side: 'below' };
  if (r.top - GAP - h >= MARGIN) return { top: r.top - GAP - h, left, arrow, side: 'above' };
  return { top: Math.min(Math.max(MARGIN, r.top + GAP), vh - h - MARGIN), left, arrow, side: 'inside' };
}

/** Liegt das Element in einer fest stehenden Leiste (sticky/fixed)? Dann nie dorthin scrollen. */
function pinned(el: HTMLElement): boolean {
  for (let n: HTMLElement | null = el; n && n !== document.body; n = n.parentElement) { const p = getComputedStyle(n).position; if (p === 'sticky' || p === 'fixed') return true; }
  return false;
}

/** Ziel finden, hervorheben, ins Bild holen und das Popup daran ausrichten. */
function useAnchor(sel: string | undefined, tipId: string | undefined, box: React.RefObject<HTMLElement>) {
  const v = useGame((st) => st.v);
  const [pos, setPos] = useState<Pos | null>(null);
  const [found, setFound] = useState(true);
  useLayoutEffect(() => {
    const el = sel ? document.querySelector<HTMLElement>(sel) : null;
    setFound(!!el);
    if (!el) { setPos(null); return; }
    el.classList.add('tour-hl');
    const r0 = el.getBoundingClientRect();
    if (!pinned(el) && (r0.bottom < 0 || r0.top > window.innerHeight - 40)) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const b = box.current; if (!b) return;
        setPos(place(el.getBoundingClientRect(), b.offsetWidth, b.offsetHeight, window.innerWidth, window.innerHeight));
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el); if (box.current) ro?.observe(box.current);
    return () => { el.classList.remove('tour-hl'); cancelAnimationFrame(raf); window.removeEventListener('scroll', update, { capture: true }); window.removeEventListener('resize', update); ro?.disconnect(); };
  }, [sel, tipId, v]); // eslint-disable-line react-hooks/exhaustive-deps
  return { pos, found };
}

/** Der nächste Tipp als Popup direkt am betreffenden Bereich. Ohne passendes Ziel als Karte über dem Spiel. */
export function Coach() {
  const { c, screen } = useTipContext();
  const { off, seen, markSeen, setOff } = useTutorial();
  const seenSet = new Set(seen);
  const tip = off || !screen ? null : nextTip(c, screen, seenSet);
  const box = useRef<HTMLElement>(null);
  const { pos, found } = useAnchor(tip?.target, tip?.id, box);
  if (!tip) return null;
  const more = TIPS.filter((t) => t.at === tip.at && t.id !== tip.id && !seenSet.has(t.id) && (!t.if || t.if(c))).length;
  const body = <>
    <span className="label">Tipp{more ? ` · noch ${more} ${more === 1 ? 'weiterer' : 'weitere'}` : ''}</span>
    <b className="coach-t">{tip.title}</b>
    <p>{textOf(tip, c)}</p>
    <div className="actions">
      <button type="button" className="small yes" onClick={() => { markSeen(tip.id); track('Tutorial.tip', { tip: tip.id }); }}>Verstanden</button>
      <button type="button" className="linkb" onClick={() => { setOff(true); track('Tutorial.disabled', { tip: tip.id, seen: seen.length }); }}>Tipps ausblenden</button>
    </div>
  </>;
  if (!tip.target || !found || typeof document === 'undefined') return <aside className="coach" aria-live="polite" aria-label="Tipp">{body}</aside>;
  return createPortal(
    <aside ref={box} className={'coach coach-pop ' + (pos?.side ?? 'below')} role="dialog" aria-live="polite" aria-label={'Tipp: ' + tip.title}
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden', ['--arrow' as any]: `${pos?.arrow ?? 24}px` }}>
      {body}
    </aside>, document.body);
}

/** „?“-Knopf mit den Tipps zum aktuellen Bildschirm. */
export function HelpButton() {
  const [open, setOpen] = useState(false);
  const { c, screen } = useTipContext();
  const { off, setOff, reset } = useTutorial();
  const list = tipsFor(c, screen);
  return <>
    <button type="button" className="small help-btn" aria-expanded={open} aria-label="Hilfe" title="Hilfe" onClick={() => { if (!open) track('Help.opened', { screen }); setOpen(!open); }}>?</button>
    {open && <div className="helppanel" role="dialog" aria-label="Hilfe">
      <div className="hp-head"><b>Hilfe zu diesem Bildschirm</b><button type="button" className="small" aria-label="Schließen" onClick={() => setOpen(false)}>✕</button></div>
      {list.length ? list.map((t) => <div key={t.id} className="hp-tip"><b>{t.title}</b><p>{textOf(t, c)}</p></div>)
        : <p className="sub">Zu diesem Bildschirm gibt es keine eigenen Tipps.</p>}
      <div className="actions">
        <label className="chk"><input type="checkbox" checked={!off} onChange={(e) => setOff(!e.target.checked)} /> Tipps beim Spielen zeigen</label>
        <button type="button" className="small" onClick={() => { reset(); track('Tutorial.reset', {}); setOpen(false); }}>Alle Tipps noch einmal zeigen</button>
      </div>
      <p className="sub"><Link to="/hilfe" onClick={() => setOpen(false)}>So funktioniert's: alle Regeln im Überblick</Link></p>
    </div>}
  </>;
}

/** Eigene Gesetze dieser Partie (ohne den Bestand der Vorgängerregierung). */
const own = (S: any): any[] => (S.laws || []).filter((L: any) => !L.bestand);
const ITEMS: { id: string; t: string; test?: (S: any) => boolean }[] = [
  { id: 'draft', t: 'Ein Gesetz in Auftrag geben', test: (S) => !!S.editing || own(S).length > 0 },
  { id: 'cabinet', t: 'Einen Entwurf ins Kabinett einbringen', test: (S) => own(S).some((L: any) => !['coord', 'draft', 'ready'].includes(L.stage)) },
  { id: 'stimmung', t: 'Im Reiter „Stimmung“ nachsehen, wie die Wählergruppen denken', test: (S) => S.tab === 'stimmung' },
  { id: 'months', t: 'Drei Monate regieren', test: (S) => S.m - S.m0 >= 3 },
  { id: 'event', t: 'Eine Krise oder Entscheidung meistern' },
  { id: 'kraft', t: 'Ein Gesetz in Kraft setzen', test: (S) => own(S).some((L: any) => L.stage === 'kraft' || L.stage === 'done') },
];

/** Checkliste für die erste Legislatur, in der rechten Spalte der Regierungsansicht. */
export function Checklist() {
  const S: any = useS();
  const { off, done, listHidden, markDone, hideList } = useTutorial();
  const pending = useRef(false);
  const show = !off && !listHidden && S.leg === 0;
  useEffect(() => {
    if (!show) return;
    // Krise oder Entscheidung: erledigt, sobald sie entschieden ist
    if (S.crisis || S.dec) pending.current = true;
    else if (pending.current) { pending.current = false; if (markDone('event')) track('Tutorial.step', { item: 'event' }); }
    for (const it of ITEMS) if (it.test?.(S) && markDone(it.id)) track('Tutorial.step', { item: it.id });
  });
  const n = ITEMS.filter((it) => done.includes(it.id)).length, all = n === ITEMS.length;
  useEffect(() => { if (show && all) track('Tutorial.completed', {}); }, [all]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!show) return null;
  return (
    <Panel title="Erste Schritte" aside={`${n} / ${ITEMS.length}`} className="checklist">
      {all ? <p>Geschafft! Du kennst jetzt die Grundlagen. Ab hier entscheidest du allein, wie du regierst.</p>
        : <ul className="plain">{ITEMS.map((it) => { const ok = done.includes(it.id); return <li key={it.id} className={ok ? 'ok' : ''}><span aria-hidden="true">{ok ? '✓' : '○'}</span> {it.t}{ok && <span className="sr-only"> (erledigt)</span>}</li>; })}</ul>}
      <button type="button" className="linkb" onClick={hideList}>{all ? 'Schließen' : 'Liste ausblenden'}</button>
    </Panel>
  );
}
